// Apple Pay as a payout method: the recipient picks a card in the Apple Pay sheet and we push
// the money to it, like "Direct to Debit Card" without typing the number.
//
// Apple encrypts the card to our Payment Processing certificate, so this page only holds an
// opaque token; it posts it straight to the CDE, the only thing that can open it. Merchant
// validation (the handshake that lets the sheet open) is also done by the CDE, which holds the
// Merchant Identity certificate.
//
// Until the Apple Developer merchant is set up the CDE answers 503 and the sheet is aborted
// with a "not set up yet" message. Apple Pay also only works in Safari on an HTTPS domain
// registered with Apple — not on localhost.
//
// Caveat: Apple Pay yields a device account number (DPAN), not the card number. NYCE hasn't
// confirmed it can push to one.
import axios from "axios";

const cdeApiUrl = process.env.REACT_APP_P2C_API_URL;

// Apple Pay JS version 14 (Safari 17+) adds disbursement requests, the "receive money" sheet.
const DISBURSEMENT_VERSION = 14;
const BASE_VERSION = 3;

export const NOT_SET_UP = "Apple Pay isn't set up yet. Please choose another option.";
export const UNSUPPORTED =
  "Apple Pay needs Safari on an iPhone, iPad, or Mac. Please choose another option.";

export const applePaySupported = () => {
  try {
    return Boolean(window.ApplePaySession && window.ApplePaySession.canMakePayments());
  } catch (err) {
    // canMakePayments throws on insecure origins (e.g. http://localhost).
    return false;
  }
};

// Opens the Apple Pay sheet. MUST be called synchronously from the click handler — Safari
// refuses to create an ApplePaySession outside the user gesture, so there is no await before
// `new ApplePaySession`.
//
// Resolves to "submitted" once the CDE has the card, "cancelled" if the recipient closed the
// sheet; rejects with an Error whose message is safe to show.
export const payOutWithApplePay = ({
  transferId,
  inviteId,
  amount,
  visaDirectEnabled,
}) =>
  new Promise((resolve, reject) => {
    if (!applePaySupported()) {
      reject(new Error(UNSUPPORTED));
      return;
    }
    if (!cdeApiUrl) {
      reject(new Error(NOT_SET_UP));
      return;
    }
    const Session = window.ApplePaySession;
    const disbursement = Session.supportsVersion(DISBURSEMENT_VERSION);

    const request = {
      countryCode: "US",
      currencyCode: "USD",
      supportedNetworks: ["visa", "masterCard", "discover"],
      merchantCapabilities: disbursement
        ? ["supports3DS", "supportsDebit", "supportsInstantFundsOut"]
        : ["supports3DS", "supportsDebit"],
      total: {
        label: "Neural Payments",
        amount: Number(amount).toFixed(2),
        type: "final",
      },
      // The name on the card isn't in the encrypted payload; the billing contact carries it.
      requiredBillingContactFields: ["name"],
    };
    if (disbursement) {
      // Unverified against a live sheet until the merchant is set up: confirm this shape
      // against Apple's ApplePayDisbursementRequest docs when testing on a real device.
      request.disbursementRequest = {};
    }

    let session;
    try {
      session = new Session(disbursement ? DISBURSEMENT_VERSION : BASE_VERSION, request);
    } catch (err) {
      reject(new Error(UNSUPPORTED));
      return;
    }

    let settled = false;
    const fail = (message) => {
      if (settled) return;
      settled = true;
      reject(new Error(message));
    };

    session.onvalidatemerchant = async (event) => {
      try {
        const { data } = await axios.post(`${cdeApiUrl}/apple_pay/merchant_session`, {
          validation_url: event.validationURL,
        });
        session.completeMerchantValidation(data);
      } catch (err) {
        session.abort();
        fail(
          err?.response?.status === 503
            ? NOT_SET_UP
            : "Apple Pay isn't available right now. Please choose another option."
        );
      }
    };

    session.onpaymentauthorized = async (event) => {
      const { token, billingContact } = event.payment;
      const cardholderName = [billingContact?.givenName, billingContact?.familyName]
        .filter(Boolean)
        .join(" ");
      try {
        await axios.post(
          `${cdeApiUrl}/apple_pay/push_to_card/${transferId}`,
          {
            invitation_id: inviteId,
            payment_data: token.paymentData,
            cardholder_name: cardholderName,
            visa_direct_enabled: visaDirectEnabled,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        session.completePayment(Session.STATUS_SUCCESS);
        settled = true;
        resolve("submitted");
      } catch (err) {
        session.completePayment(Session.STATUS_FAILURE);
        const message = err?.response?.data;
        fail(
          message === "This debit card number is not supported."
            ? "That card can't receive this payment. Please pick a different card or choose another option."
            : "We couldn't use that card. Please try again or choose another option."
        );
      }
    };

    session.oncancel = () => {
      if (settled) return;
      settled = true;
      resolve("cancelled");
    };

    session.begin();
  });
